import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { kgToBoxes } from '../../utils/packaging';

interface SendModalState {
  isOpen: boolean;
  offerId: string;
  offerNumber: string;
  buyerName: string;
}

const initialSendModal: SendModalState = { isOpen: false, offerId: '', offerNumber: '', buyerName: '' };

export const OfferList: React.FC = () => {
  const [offers, setOffers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [sendModal, setSendModal] = useState<SendModalState>(initialSendModal);
  const [sendEmail, setSendEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const load = () => {
    fetch('/api/admin/offers', { credentials: 'include' })
      .then(r => r.json())
      .then(data => { setOffers(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async (id: string, number: string) => {
    if (!confirm(`Delete offer ${number}?`)) return;
    await fetch('/api/admin/offers', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ id }),
    });
    setOffers(prev => prev.filter(o => o.id !== id));
  };

  const openSendModal = (offer: any) => {
    setSendModal({ isOpen: true, offerId: offer.id, offerNumber: offer.offerNumber, buyerName: offer.buyerName || 'Unassigned' });
    setSendEmail('');
    setSendError('');
    setSending(false);
  };

  const closeSendModal = () => {
    setSendModal(initialSendModal);
    setSendError('');
  };

  const handleSend = async () => {
    setSending(true);
    setSendError('');
    try {
      const res = await fetch(`/api/admin/offers?id=${sendModal.offerId}&action=send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: sendEmail.trim() || undefined }),
      });
      if (res.ok) {
        closeSendModal();
        load();
      } else {
        const data = await res.json().catch(() => ({}));
        setSendError(data.error || 'Failed to send offer. Please try again.');
      }
    } catch {
      setSendError('Network error. Please check your connection.');
    } finally {
      setSending(false);
    }
  };

  const fmtCurrency = (amount: number, currency: string = 'USD') => {
    const symbols: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', KES: 'KES ' };
    const sym = symbols[currency] || currency + ' ';
    return `${sym}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">Offers</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">{offers.length} total offers</p>
        </div>
        <Link to="/admin/offers/new" className="px-5 py-2.5 bg-[#1A1A1A] text-white rounded-xl text-sm font-medium hover:bg-[#2a2a2a] active:scale-[0.98] transition-all shadow-sm shadow-black/10">
          + New Offer
        </Link>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-white rounded-2xl animate-pulse shadow-sm" />)}
        </div>
      ) : (
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl overflow-hidden shadow-sm shadow-black/[0.03]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8E6E2]/60 bg-[#FAFAF8]/50">
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Offer</th>
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Buyer</th>
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Quantity</th>
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Value</th>
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Status</th>
                <th className="px-5 py-3.5 text-right text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {offers.map(offer => (
                <tr key={offer.id} className="border-b border-[#E8E6E2]/60 last:border-0 hover:bg-[#FAFAF8]/50 transition-colors">
                  <td className="px-5 py-4 font-semibold text-[#1A1A1A]">{offer.offerNumber}</td>
                  <td className="px-5 py-4 text-[#1A1A1A]">{offer.buyerName || <span className="text-[#C4C4C4]">Unassigned</span>}</td>
                  <td className="px-5 py-4 text-[#6B6B6B] tabular-nums">
                    {parseFloat(offer.totalQuantityKg).toLocaleString('en-US')} kg
                    <span className="text-[#C4C4C4]"> &middot; {kgToBoxes(parseFloat(offer.totalQuantityKg)).toLocaleString('en-US')} boxes</span>
                  </td>
                  <td className="px-5 py-4 font-semibold text-[#1A1A1A] tabular-nums">{fmtCurrency(parseFloat(offer.totalValue), offer.currency)}</td>
                  <td className="px-5 py-4"><StatusBadge status={offer.status} /></td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/admin/offers/${offer.id}`} className="px-2.5 py-1.5 text-[12px] font-medium text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EDE8] rounded-lg transition-colors">
                        {offer.status === 'accepted' ? 'View' : 'Edit'}
                      </Link>
                      {offer.status === 'draft' && (
                        <>
                          <button onClick={() => openSendModal(offer)} className="px-2.5 py-1.5 text-[12px] font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors">Send</button>
                          <button onClick={() => handleDelete(offer.id, offer.offerNumber)} className="px-2.5 py-1.5 text-[12px] font-medium text-[#C4C4C4] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">Delete</button>
                        </>
                      )}
                      {(offer.status === 'sent' || offer.status === 'declined') && (
                        <button onClick={() => openSendModal(offer)} className="px-2.5 py-1.5 text-[12px] font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors">
                          {offer.status === 'declined' ? 'Amend & Resend' : 'Resend'}
                        </button>
                      )}
                      {offer.status === 'accepted' && offer.contractId && (
                        <Link to={`/admin/contracts/${offer.contractId}`} className="px-2.5 py-1.5 text-[12px] font-medium text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded-lg transition-colors">View Contract</Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {offers.length === 0 && (
            <div className="p-12 text-center"><p className="text-[#A3A3A3]">No offers yet. Create your first one.</p></div>
          )}
        </div>
      )}

      {sendModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeSendModal} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden animate-scale-in">
            <div className="px-6 py-5 border-b border-[#E8E6E2]/60">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-[#1A1A1A]">Send Offer</h2>
                  <p className="text-sm text-[#A3A3A3] mt-0.5">{sendModal.offerNumber} &middot; {sendModal.buyerName}</p>
                </div>
                <a
                  href={`/api/admin/offers?id=${sendModal.offerId}&action=email-preview`}
                  target="_blank"
                  rel="noopener"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#6B6B6B] hover:text-[#1A1A1A] bg-white border border-[#E8E6E2] rounded-lg hover:border-[#1A1A1A]/30 transition-all"
                  title="Preview the email body before sending"
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1.5 2.5h9v7h-9v-7z" stroke="currentColor" strokeWidth="1.1"/><path d="M1.5 3l4.5 3.5L10.5 3" stroke="currentColor" strokeWidth="1.1"/></svg>
                  Email
                </a>
              </div>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-[11px] font-semibold text-[#6B6B6B] uppercase tracking-[0.12em] mb-1.5">Recipient Email(s)</label>
                <input
                  type="text"
                  value={sendEmail}
                  onChange={e => { setSendEmail(e.target.value); setSendError(''); }}
                  placeholder="Leave blank to use buyer's contact email, or enter multiple separated by commas"
                  className="w-full px-4 py-2.5 border border-[#E8E6E2] rounded-xl text-sm text-[#1A1A1A] placeholder-[#C4C4C4] bg-[#FAFAF8] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/10 focus:border-[#1A1A1A]/30 focus:bg-white transition-all"
                  autoFocus
                />
                <p className="text-xs text-[#A3A3A3] mt-1.5">Separate multiple addresses with commas.</p>
              </div>
              {sendError && (
                <div className="p-3.5 bg-red-50 border border-red-100 rounded-xl"><p className="text-sm text-red-600 font-medium">{sendError}</p></div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-[#E8E6E2]/60 bg-[#FAFAF8]/50 flex items-center justify-end gap-2">
              <button onClick={closeSendModal} disabled={sending} className="px-4 py-2 text-sm font-medium text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-white rounded-lg border border-[#E8E6E2] transition-all">Cancel</button>
              <button onClick={handleSend} disabled={sending} className="px-5 py-2 text-sm font-medium text-white bg-[#1A1A1A] rounded-xl hover:bg-[#2a2a2a] disabled:opacity-40 transition-all shadow-sm shadow-black/10">
                {sending ? 'Sending...' : 'Send Offer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
